import qz from 'qz-tray';
import { escapeHtml } from '@/utils/html.js';
import { useConfigurationStore } from '@/stores/configuration-store.js';

qz.security.setCertificatePromise((resolve) => resolve(''));
qz.security.setSignatureAlgorithm('SHA512');
qz.security.setSignaturePromise(() => (resolve) => resolve(''));

/** null = unknown, true/false = probed this session */
let qzAvailable = null;
let connectPromise = null;

/**
 * The paper stocks this application prints on. 58mm and 80mm are till rolls, so
 * they are continuous (`height: 0`) and the receipt ends where its content does.
 * A4 is a cut sheet and needs a real page height for QZ to paginate it.
 */
const PAPER = {
    '58MM': { width: 58, height: 0, isSheet: false },
    '80MM': { width: 80, height: 0, isSheet: false },
    A4: { width: 210, height: 297, isSheet: true },
};

/**
 * Which configuration row names the printer for a given stock. Two rows rather
 * than one per document: a shop has at most a till roll and an office printer,
 * and asking an operator to name the same printer eight times invites drift.
 */
const PRINTER_SETTING = {
    sheet: 'a4_printer_name',
    roll: 'thermal_printer_name',
};

/**
 * Resolve a print request to a paper stock.
 *
 * `size` is the configured document size ('58mm' | '80mm' | 'A4'); `pageWidth`
 * is the millimetre width the HTML was laid out at, which older call sites pass
 * instead. Anything at least as wide as A4 is a sheet, not a roll.
 */
function resolvePaper(size, pageWidth) {
    const key = String(size ?? '').trim().toUpperCase();
    if (PAPER[key]) return PAPER[key];

    const parsed = typeof pageWidth === 'string' ? parseFloat(pageWidth) : Number(pageWidth);
    const width = Number.isFinite(parsed) && parsed > 0 ? parsed : PAPER['80MM'].width;

    return width >= PAPER.A4.width ? PAPER.A4 : { width, height: 0, isSheet: false };
}

/**
 * The printer the operator picked for this stock, if any. Read defensively:
 * this module is also imported by code paths that run before Pinia is active.
 */
function configuredPrinterName(isSheet) {
    try {
        const configurations = useConfigurationStore().configurations ?? {};
        return String(configurations[PRINTER_SETTING[isSheet ? 'sheet' : 'roll']] ?? '').trim();
    } catch {
        return '';
    }
}

/**
 * A named printer that is switched off or renamed must not stop the job — it
 * falls back to the system default, which is what an unconfigured install uses.
 */
async function resolvePrinter(isSheet) {
    const configured = configuredPrinterName(isSheet);
    if (configured) {
        try {
            const match = await qz.printers.find(configured);
            const name = Array.isArray(match) ? match[0] : match;
            if (name) return name;
        } catch {
            /* named printer is gone — fall through to the default */
        }
    }

    return qz.printers.getDefault();
}

/**
 * Stylesheets and images have to be in before the dialog opens, or the browser
 * prints the document unstyled. Capped, because a print must never hang on an
 * asset that is never going to arrive.
 */
function waitForFrameAssets(frameWindow) {
    const pending = Array.from(
        frameWindow.document.querySelectorAll('link[rel="stylesheet"], img'),
    ).filter((node) => !(node.tagName === 'IMG' && node.complete));

    if (!pending.length) return Promise.resolve();

    return new Promise((resolve) => {
        let remaining = pending.length;
        const settle = () => {
            remaining -= 1;
            if (remaining <= 0) resolve();
        };
        pending.forEach((node) => {
            node.addEventListener('load', settle, { once: true });
            node.addEventListener('error', settle, { once: true });
        });
        setTimeout(resolve, 1500);
    });
}

/**
 * Open HTML in a hidden iframe and trigger the browser print dialog.
 *
 * In-place on the current page by design — never a popup or a second tab, so
 * the dialog appears where the operator clicked and no popup blocker is in play.
 */
export async function printHtmlInHiddenIframe(html) {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.opacity = '0';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);

    const frameWindow = iframe.contentWindow;
    if (!frameWindow) {
        iframe.remove();
        return;
    }

    const doc = frameWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    await waitForFrameAssets(frameWindow);

    frameWindow.focus();
    frameWindow.print();

    setTimeout(() => iframe.remove(), 1500);
}

/**
 * Try to connect to QZ Tray once per session.
 * On failure, remembers that QZ is unavailable so later prints skip the WebSocket probe.
 */
export async function ensureQzConnected() {
    if (qz.websocket.isActive()) {
        qzAvailable = true;
        return true;
    }
    if (qzAvailable === false) {
        return false;
    }
    if (connectPromise) {
        return connectPromise;
    }

    connectPromise = (async () => {
        try {
            // Probe only the default QZ port once. Browser still logs one WebSocket
            // failure when QZ is down; we cache that so later prints skip the probe.
            await qz.websocket.connect({
                host: ['localhost'],
                usingSecure: true,
                port: { secure: [8181], insecure: [8182] },
                retries: 0,
                delay: 0,
            });
            qzAvailable = true;
            return true;
        } catch {
            qzAvailable = false;
            return false;
        } finally {
            connectPromise = null;
        }
    })();

    return connectPromise;
}

/**
 * Printers QZ can see, for the settings screen to offer. Empty when QZ is not
 * running, which is also the answer for an install that never prints directly.
 *
 * @returns {Promise<string[]>}
 */
export async function listQzPrinters() {
    if (!(await ensureQzConnected())) return [];

    try {
        const found = await qz.printers.find();
        return (Array.isArray(found) ? found : [found]).filter(Boolean).map(String);
    } catch {
        return [];
    }
}

/**
 * The one way this application prints.
 *
 * QZ Tray when it is running — the job goes straight to the printer with no
 * dialog — and the browser print dialog in a hidden iframe otherwise. Every
 * document goes through here, whatever its paper stock, so an operator sees the
 * same behaviour from the till, a detail screen or a report.
 *
 * Never throws for a missing QZ: that is the expected state on any machine
 * without the tray app installed.
 *
 * @param {string} html a complete, self-contained HTML document
 * @param {object} [options]
 * @param {string} [options.size] '58mm' | '80mm' | 'A4'
 * @param {number|string} [options.pageWidth] millimetres, when no `size` is known
 * @param {object} [options.config] extra qz.configs.create options
 * @returns {Promise<'qz'|'browser'>}
 */
export async function printDocument(html, { size, pageWidth, config: configExtras = {} } = {}) {
    const connected = await ensureQzConnected();
    if (!connected) {
        await printHtmlInHiddenIframe(html);
        return 'browser';
    }

    const paper = resolvePaper(size, pageWidth);

    try {
        const printer = await resolvePrinter(paper.isSheet);
        if (!printer) {
            await printHtmlInHiddenIframe(html);
            return 'browser';
        }

        const config = qz.configs.create(printer, {
            size: { width: paper.width, height: paper.height },
            units: 'mm',
            colorType: paper.isSheet ? 'color' : 'blackwhite',
            margins: { top: 0, right: 0, bottom: 0, left: 0 },
            // A sheet is laid out for a fixed page and has to be fitted to it;
            // a till roll is already the width of its paper and must not be
            // rescaled, or the font stops matching the printer's own metrics.
            ...(paper.isSheet ? { orientation: 'portrait', scaleContent: true } : {}),
            ...configExtras,
        });

        await qz.print(config, [{ type: 'html', format: 'plain', data: html }]);
        return 'qz';
    } catch {
        await printHtmlInHiddenIframe(html);
        return 'browser';
    }
}

/**
 * Print a live element rather than a generated document, for screens whose
 * printable output is the rendered page itself. The element is lifted into a
 * standalone document carrying the app's stylesheets so it prints as it looks.
 *
 * @param {HTMLElement|null} element
 * @param {object} [options] as printDocument
 * @returns {Promise<'qz'|'browser'>}
 */
export async function printElement(element, options = {}) {
    if (!element) return 'browser';

    // Screen-only controls are removed rather than hidden: `@media print` rules
    // are honoured by the browser dialog but not reliably by QZ's renderer, and
    // a Print button coming out of the printer is the tell.
    const printable = element.cloneNode(true);
    printable.querySelectorAll('.no-print, [data-print-exclude]').forEach((node) => node.remove());

    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
        .map((node) =>
            node.tagName === 'STYLE'
                ? `<style>${node.textContent}</style>`
                : `<link rel="stylesheet" href="${escapeHtml(node.href)}">`,
        )
        .join('\n');

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
${styles}
</head>
<body>${printable.outerHTML}</body>
</html>`;

    return printDocument(html, options);
}
